import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import {
    selectNumberOfItemsForBadgeInAgentView,
    selectShouldSeeTicketsTab,
} from 'flow/selectors/TicketsSelectors';

import { routes } from 'flow/types/routes';
import { calcTotalUnreadMessages } from 'flow/utils';
import { calcTotalUnreadVoiceMessages } from 'flow/utils/extension-utils';
import {
    calcTotalUnreadChannelMessages,
    calcTotalUnreadVoiceMessagesForVoicemails,
} from 'flow/utils/pbx-utils';
import { generatePath } from 'flow/utils/router-utils';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { NavLink } from 'react-router-dom';
import Icon, { IconKey } from './Icon';
import Notification from './Notification';
import Tooltip from './Tooltip';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import { selectNumberOfCentionErrandsRelevantForUser } from 'flow/selectors/CentionOmniSelectors';
import useHasAiStudioAccess from 'flow/pages/ai-studio/hooks/useHasAiStudioAccess';
import { isSlimmedTeamsApp } from 'flow/utils/app-utils';

/**
 * Marks a main navigation tab in the DOM, so behaviour outside this component
 * can recognise one — see dropFocusNotAskedFor in
 * route-components/ContextComponent.
 *
 * Deliberately not the `data-test-id`, which should stay free to change with
 * tests.
 */
export const NAV_TAB_ATTRIBUTE = 'data-nav-tab';

type Tabs =
    | 'extensions'
    | 'messages'
    | 'pbx'
    | 'calls'
    | 'omni'
    | 'tickets'
    | 'settings'
    | 'ai-studio';

interface Tab {
    iconKey: IconKey;
    key: Tabs;
    tooltip: string;
    badgeCounter?: number;
    route: string;
}

const ticketsTab: Tab = {
    iconKey: 'headset',
    key: 'tickets',
    tooltip: 'route.tickets',
    route: routes['tickets'],
};

const aiStudioTab: Tab = {
    iconKey: 'autoAwesome',
    key: 'ai-studio',
    tooltip: 'route.ai-studio',
    route: routes['ai-studio.page'],
};

const omniTab: Tab = {
    iconKey: 'support_agent',
    key: 'omni',
    tooltip: 'route.omni',
    route: routes['omni'],
};

const messagesTab: Tab = {
    iconKey: 'chat',
    key: 'messages',
    tooltip: 'route.messages',
    route: routes['messages'],
};

const pbxTab: Tab = {
    iconKey: 'ivr',
    key: 'pbx',
    tooltip: 'route.pbx',
    route: routes['pbx'],
};

const AppNavigation = () => {
    const { embedded, i18next } = useAppContext();
    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { hasUserAccessAiStudio } = useHasAiStudioAccess();

    const sessions = useAppSelector((state) => state.chats.get('sessions'));

    const channelsMap = useAppSelector((state) =>
        state.channels.get('channels'),
    );

    const chatUserKey = useAppSelector((state) =>
        state.app.getIn(['extension', 'chatUserKey']),
    );

    const missedCallCount = useAppSelector((state) =>
        state.app.get('missedCallCount'),
    );

    const sharedVoicemails = useAppSelector((state) =>
        state.pbx.get('voicemails'),
    );

    const shouldSeeTicketsTab = useAppSelector(selectShouldSeeTicketsTab);

    const ticketsAssignedToUserCount = useAppSelector(
        (state) => selectNumberOfItemsForBadgeInAgentView(state) || 0,
    );

    const showOmnichannelItem = useAppSelector(
        loggedInUserHasOmnichannelFeature,
    );

    const totalNumberOfOmnichannelErrands = useAppSelector((state) =>
        showOmnichannelItem
            ? selectNumberOfCentionErrandsRelevantForUser(state)
            : 0,
    );

    const badges = {
        messages: calcTotalUnreadMessages(sessions),
        pbx:
            calcTotalUnreadChannelMessages(channelsMap, chatUserKey) +
            calcTotalUnreadVoiceMessagesForVoicemails(sharedVoicemails),
        calls:
            calcTotalUnreadVoiceMessages(ownExtension.get('voiceMessages')) +
            missedCallCount,
        tickets: ticketsAssignedToUserCount,
        omni: totalNumberOfOmnichannelErrands
            ? totalNumberOfOmnichannelErrands
            : 0,
    } as Record<Tabs, number>;

    const slimmed = isSlimmedTeamsApp(embedded);

    const tabs: Tab[] = [
        {
            iconKey: 'accountBox',
            key: 'extensions',
            tooltip: 'route.extensions',
            route: routes['extensions'],
        },
        ...(slimmed ? [] : [messagesTab, pbxTab]),
        {
            iconKey: 'callHistory',
            key: 'calls',
            tooltip: 'route.calls',
            route: routes['calls'],
        },
        ...(shouldSeeTicketsTab ? [ticketsTab] : []),
        ...(showOmnichannelItem ? [omniTab] : []),
        ...(hasUserAccessAiStudio ? [aiStudioTab] : []),
        {
            iconKey: 'general',
            key: 'settings',
            tooltip: 'route.settings',
            route: generatePath(routes['settings'], {
                extensionKey: ownExtensionKey,
            }),
        },
    ];

    return (
        <nav className="flex items-center rounded-sm bg-gray-200">
            {tabs.map((tab) => {
                const label = i18next.t(tab.tooltip);

                return (
                    <Tooltip key={tab.key} label={label}>
                        <NavLink
                            data-test-id={`tab-${tab.key}`}
                            {...{ [NAV_TAB_ATTRIBUTE]: '' }}
                            to={tab.route}
                            className={twMerge(
                                'hover:darken-10 focus-ring relative flex items-center bg-gray-200 px-4 py-2 text-gray-500 first:rounded-s-sm last:rounded-e-sm hover:rounded-sm hover:text-gray-600 focus-visible:z-10',
                            )}
                            activeClassName="bg-main text-iconOnBrand rounded-sm hover:text-white dark:hover:!text-gray-800"
                        >
                            <Icon
                                iconKey={tab.iconKey}
                                className={
                                    ['messages'].includes(tab.key)
                                        ? 'rtl:scale-x-[-1]'
                                        : ''
                                }
                            />

                            {/*
                             * The icon is decorative, so this hidden text is
                             * the link's whole name. Text rather than an
                             * aria-label, so the badge count that follows
                             * stays part of the name: "Tickets 5", not "5".
                             */}
                            <span className="sr-only">{label}</span>

                            {badges[tab.key] ? (
                                <Notification className="me-1">
                                    {badges[tab.key] < 100
                                        ? badges[tab.key]
                                        : '99+'}
                                </Notification>
                            ) : null}
                        </NavLink>
                    </Tooltip>
                );
            })}
        </nav>
    );
};

export default AppNavigation;
